import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  FiUser, FiCamera, FiSave, FiCreditCard,
  FiMapPin, FiLock, FiKey, FiBriefcase, FiGlobe, FiSearch, FiTrash2, FiAlertTriangle
} from 'react-icons/fi'
import axios from 'axios'
import toast from 'react-hot-toast'
import currencies from '../../utils/currencies'
import { WORLD_TIMEZONES, searchTimezones } from '../../utils/timezones'

export default function UserAdminSettings() {
  const navigate = useNavigate()
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('profile')
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deletePassword, setDeletePassword] = useState('')
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  const [profileData, setProfileData] = useState({ firstName: '', lastName: '', profileImageUrl: '' })
  const [businessData, setBusinessData] = useState({ businessName: '', phoneNumber: '', address: '', description: '' })
  const [bankData, setBankData] = useState({
    bankName: '',
    accountName: '',
    accountNumber: '',
    country: '',
    currency: '',
    IBAN: '',
    SWIFT: '',
    routingNumber: '',
    sortCode: '',
    IFSC: '',
    BSB: '',
    transitNumber: '',
    bankCode: '',
    branchName: '',
    branchAddress: '',
    accountType: '',
    otherIdentifiers: ''
  })
  const [addressData, setAddressData] = useState({ street: '', city: '', state: '', zipCode: '', country: '' })
  const [maskedBankData, setMaskedBankData] = useState({})
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' })
  const [adminPasswordData, setAdminPasswordData] = useState({ currentAdminPassword: '', newAdminPassword: '', confirmAdminPassword: '' })
  const [adminResetData, setAdminResetData] = useState({ currentAdminPassword: '', newLoginPassword: '', confirmNewLoginPassword: '' })
  const [notepadPasswordData, setNotepadPasswordData] = useState({ generatedPassword: '', showPassword: false, showResetForm: false, hasPasswordSet: false, createdAt: null })
  const [notepadResetData, setNotepadResetData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '', showCurrent: false, showNew: false })
  const [preferredTimezone, setPreferredTimezone] = useState(user?.preferredTimezone || 'UTC')
  const [timezoneSearch, setTimezoneSearch] = useState('')
  const [showTimezoneDropdown, setShowTimezoneDropdown] = useState(false)
  const [profileImageFile, setProfileImageFile] = useState(null)
  const [updating, setUpdating] = useState(false)
  const [companyLogo, setCompanyLogo] = useState(null)
  const [logoFile, setLogoFile] = useState(null)

  const handleDeleteAccount = async (e) => {
    e.preventDefault()

    if (!deletePassword) {
      toast.error('Password is required')
      return
    }

    if (deleteConfirmText !== 'DELETE MY ACCOUNT') {
      toast.error('Please type "DELETE MY ACCOUNT" to confirm')
      return
    }

    setIsDeleting(true)
    try {
      await axios.delete('/users/delete-account', {
        data: { password: deletePassword }
      })

      toast.success('Your account has been permanently deleted')
      localStorage.removeItem('token')
      localStorage.removeItem('userAdminToken')
      setTimeout(() => navigate('/'), 2000)
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error deleting account')
    } finally {
      setIsDeleting(false)
    }
  }

  useEffect(() => {
    fetchProfile()
    fetchNotepadPasswordStatus()
  }, [])

  const fetchNotepadPasswordStatus = async () => {
    try {
      const response = await axios.get('/admin/notepad-password/check')
      setNotepadPasswordData(prev => ({
        ...prev,
        hasPasswordSet: response.data.hasPassword,
        createdAt: response.data.createdAt
      }))
    } catch (error) {
      console.error('Error fetching notepad password status:', error)
    }
  }

  const fetchProfile = async () => {
    try {
      const response = await axios.get('/auth/profile')
      const userData = response.data.user
      setUser(userData)
      setPreferredTimezone(userData.preferredTimezone || 'UTC')

      setProfileData({
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        profileImageUrl: userData.profileImage || ''
      })

      setBusinessData({
        businessName: userData.businessInfo?.businessName || '',
        phoneNumber: userData.businessInfo?.phoneNumber || '',
        address: userData.businessInfo?.address || '',
        description: userData.businessInfo?.description || ''
      })

      // For security, server returns masked bank values; do NOT populate the editable inputs with masked placeholders as actual values.
      setMaskedBankData(userData.bankDetails || {})
      setBankData({
        bankName: '',
        accountName: '',
        accountNumber: '',
        country: '',
        currency: '',
        IBAN: '',
        SWIFT: '',
        routingNumber: '',
        sortCode: '',
        IFSC: '',
        BSB: '',
        transitNumber: '',
        bankCode: '',
        branchName: '',
        branchAddress: '',
        accountType: '',
        otherIdentifiers: ''
      })

      setAddressData({
        street: userData.billingAddress?.street || '',
        city: userData.billingAddress?.city || '',
        state: userData.billingAddress?.state || '',
        zipCode: userData.billingAddress?.zipCode || '',
        country: userData.billingAddress?.country || ''
      })

      setCompanyLogo(userData.companyLogo || null)
    } catch {
      toast.error('Error loading profile')
    } finally {
      setLoading(false)
    }
  }

  const handleProfileUpdate = async e => {
    e.preventDefault()
    setUpdating(true)
    try {
      const formData = new FormData()
      formData.append('firstName', profileData.firstName)
      formData.append('lastName', profileData.lastName)
      formData.append('profileImageUrl', profileData.profileImageUrl)
      if (profileImageFile) formData.append('profileImage', profileImageFile)

      const response = await axios.put('/users/profile', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      setUser(response.data.user)
      toast.success('Profile updated successfully')
      setProfileImageFile(null)
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error updating profile')
    } finally {
      setUpdating(false)
    }
  }

  const handleBusinessUpdate = async e => {
    e.preventDefault()
    setUpdating(true)
    try {
      await axios.put('/users/business-info', businessData)
      toast.success('Business information updated successfully')
      fetchProfile()
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error updating business information')
    } finally {
      setUpdating(false)
    }
  }

  const handleBankUpdate = async e => {
    e.preventDefault()
    setUpdating(true)
    try {
      // Only send fields which have been explicitly entered (avoid sending masked placeholders)
      const payload = {}
      Object.keys(bankData).forEach(k => {
        if (bankData[k] !== '' && bankData[k] !== null && typeof bankData[k] !== 'undefined') {
          payload[k] = bankData[k]
        }
      })

      if (Object.keys(payload).length === 0) {
        toast('No changes to save')
        setUpdating(false)
        return
      }

      await axios.put('/users/bank-details', payload)
      toast.success('Bank details updated successfully')
      // Clear local form after update
      setBankData(prev => Object.fromEntries(Object.keys(prev).map(k => [k, ''])))
      // Clear masked data display to ensure clean state
      setMaskedBankData({})
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error updating bank details')
    } finally {
      setUpdating(false)
    }
  }

  const handleAddressUpdate = async e => {
    e.preventDefault()
    setUpdating(true)
    try {
      await axios.put('/users/billing-address', addressData)
      toast.success('Billing address updated successfully')
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error updating billing address')
    } finally {
      setUpdating(false)
    }
  }

  const handlePasswordChange = async e => {
    e.preventDefault()
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match')
      return
    }
    if (passwordData.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters long')
      return
    }
    setUpdating(true)
    try {
      await axios.put('/users/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      })
      toast.success('Password changed successfully')
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' })
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error changing password')
    } finally {
      setUpdating(false)
    }
  }

  const handleAdminPasswordChange = async e => {
    e.preventDefault()
    if (adminPasswordData.newAdminPassword !== adminPasswordData.confirmAdminPassword) {
      toast.error('New admin passwords do not match')
      return
    }
    if (adminPasswordData.newAdminPassword.length < 6) {
      toast.error('New admin password must be at least 6 characters long')
      return
    }
    setUpdating(true)
    try {
      await axios.put('/admin/change-password', {
        currentAdminPassword: adminPasswordData.currentAdminPassword,
        newAdminPassword: adminPasswordData.newAdminPassword
      })
      toast.success('Admin password changed successfully')
      setAdminPasswordData({ currentAdminPassword: '', newAdminPassword: '', confirmAdminPassword: '' })
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error changing admin password')
    } finally {
      setUpdating(false)
    }
  }

  const handleAdminResetLogin = async e => {
    e.preventDefault()
    if (adminResetData.newLoginPassword !== adminResetData.confirmNewLoginPassword) {
      toast.error('New passwords do not match')
      return
    }
    if (adminResetData.newLoginPassword.length < 6) {
      toast.error('New login password must be at least 6 characters long')
      return
    }
    setUpdating(true)
    try {
      await axios.put('/admin/reset-login-password', {
        currentAdminPassword: adminResetData.currentAdminPassword,
        newLoginPassword: adminResetData.newLoginPassword
      })
      toast.success('Login password reset successfully')
      setAdminResetData({ currentAdminPassword: '', newLoginPassword: '', confirmNewLoginPassword: '' })
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error resetting login password')
    } finally {
      setUpdating(false)
    }
  }

  const handleSetNotepadPassword = async e => {
    e.preventDefault()
    setUpdating(true)
    try {
      const response = await axios.post('/admin/notepad-password/set')
      toast.success('Notepad password created successfully')
      
      // Display the generated password
      setNotepadPasswordData(prev => ({
        ...prev,
        generatedPassword: response.data.notepadPassword,
        showPassword: true,
        hasPasswordSet: true,
        createdAt: new Date().toISOString()
      }))
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error setting notepad password')
    } finally {
      setUpdating(false)
    }
  }

  const handleResetNotepadPassword = async e => {
    e.preventDefault()
    
    // Validate inputs
    if (!notepadResetData.currentPassword || !notepadResetData.newPassword || !notepadResetData.confirmPassword) {
      toast.error('All fields are required')
      return
    }

    if (notepadResetData.newPassword !== notepadResetData.confirmPassword) {
      toast.error('New passwords do not match')
      return
    }

    if (notepadResetData.newPassword.length < 1) {
      toast.error('New password is required')
      return
    }

    if (notepadResetData.currentPassword === notepadResetData.newPassword) {
      toast.error('New password must be different from current password')
      return
    }

    setUpdating(true)
    try {
      const response = await axios.post('/admin/notepad-password/reset', {
        currentPassword: notepadResetData.currentPassword,
        newPassword: notepadResetData.newPassword
      })

      toast.success('Notepad password reset successfully')
      
      // Update status
      setNotepadPasswordData(prev => ({
        ...prev,
        hasPasswordSet: true,
        createdAt: response.data.createdAt,
        showResetForm: false
      }))

      // Clear reset form
      setNotepadResetData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        showCurrent: false,
        showNew: false
      })
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error resetting notepad password')
    } finally {
      setUpdating(false)
    }
  }

  const copyNotepadPassword = () => {
    navigator.clipboard.writeText(notepadPasswordData.generatedPassword)
    toast.success('Password copied to clipboard')
  }

  const handleImageFileChange = e => {
    const file = e.target.files[0]
    if (!file) return
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB')
      return
    }
    setProfileImageFile(file)
    const reader = new FileReader()
    reader.onload = e => setProfileData(prev => ({ ...prev, profileImageUrl: e.target.result }))
    reader.readAsDataURL(file)
  }

  const handleTimezoneUpdate = async e => {
    e.preventDefault()
    setUpdating(true)
    try {
      const response = await axios.put('/auth/profile', { preferredTimezone })
      setUser(response.data.user)
      toast.success('Timezone preference updated successfully')
      setTimezoneSearch('')
      setShowTimezoneDropdown(false)
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error updating timezone preference')
    } finally {
      setUpdating(false)
    }
  }

  const handleLogoFileChange = e => {
    const file = e.target.files[0]
    if (!file) return
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB')
      return
    }
    setLogoFile(file)
    const reader = new FileReader()
    reader.onload = e => setCompanyLogo(e.target.result)
    reader.readAsDataURL(file)
  }

  const handleLogoUpload = async e => {
    e.preventDefault()
    if (!logoFile) {
      toast.error('Please select a logo file')
      return
    }

    setUpdating(true)
    try {
      const formData = new FormData()
      formData.append('companyLogo', logoFile)

      const response = await axios.post('/users/company-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })

      setCompanyLogo(response.data.companyLogo)
      setLogoFile(null)
      toast.success('Company logo uploaded successfully')
      
      // Reset file input
      const fileInput = document.getElementById('logo-file-input')
      if (fileInput) fileInput.value = ''
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error uploading company logo')
    } finally {
      setUpdating(false)
    }
  }

  const handleLogoDelete = async e => {
    e.preventDefault()
    setUpdating(true)
    try {
      await axios.delete('/users/company-logo')
      setCompanyLogo(null)
      setLogoFile(null)
      toast.success('Company logo deleted successfully')
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error deleting company logo')
    } finally {
      setUpdating(false)
    }
  }

  const filteredTimezones = timezoneSearch ? searchTimezones(timezoneSearch) : WORLD_TIMEZONES

  const tabs = [
    { id: 'profile', name: 'Profile Info', icon: FiUser },
    { id: 'business', name: 'Business Info', icon: FiBriefcase },
    { id: 'bank', name: 'Bank Details', icon: FiCreditCard },
    { id: 'address', name: 'Billing Address', icon: FiMapPin },
    { id: 'password', name: 'Change Password', icon: FiLock },
    { id: 'admin-password', name: 'Admin Password', icon: FiKey },
    { id: 'notepad-security', name: 'Notepad Security', icon: FiLock },
    { id: 'company-logo', name: 'Company Logo', icon: FiBriefcase },
    { id: 'preferences', name: 'Preferences', icon: FiGlobe },
    { id: 'account-security', name: 'Account & Security', icon: FiAlertTriangle }
  ]

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="card h-96 bg-gray-200"></div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Admin Settings</h1>
        <p className="text-gray-600">Manage all profile information and settings as User Admin</p>
      </div>

      <div className="card">
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8 overflow-x-auto">
            {tabs.map(tab => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {tab.name}
                </button>
              )
            })}
          </nav>
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <form onSubmit={handleProfileUpdate} className="space-y-6">
            <div className="flex items-center space-x-6">
              <div className="relative">
                <img
                  src={
                    profileData.profileImageUrl ||
                    `https://ui-avatars.com/api/?name=${profileData.firstName}+${profileData.lastName}&background=3b82f6&color=fff`
                  }
                  alt="Profile"
                  className="w-24 h-24 rounded-full object-cover border-4 border-gray-200"
                />
                <label className="absolute bottom-0 right-0 bg-primary-600 text-white p-2 rounded-full cursor-pointer hover:bg-primary-700 transition-colors">
                  <FiCamera className="w-4 h-4" />
                  <input type="file" accept="image/*" onChange={handleImageFileChange} className="hidden" />
                </label>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium text-gray-900">Profile Picture</h3>
                <p className="text-sm text-gray-600">Upload a new profile picture or enter an image URL</p>
                <div className="mt-2">
                  <input
                    type="url"
                    placeholder="Or enter image URL"
                    value={profileData.profileImageUrl}
                    onChange={e => setProfileData(prev => ({ ...prev, profileImageUrl: e.target.value }))}
                    className="input-field"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                <input
                  type="text"
                  required
                  value={profileData.firstName}
                  onChange={e => setProfileData(prev => ({ ...prev, firstName: e.target.value }))}
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                <input
                  type="text"
                  required
                  value={profileData.lastName}
                  onChange={e => setProfileData(prev => ({ ...prev, lastName: e.target.value }))}
                  className="input-field"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
              <input type="email" value={user?.email || ''} disabled className="input-field bg-gray-50 cursor-not-allowed" />
              <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
            </div>

            <button type="submit" disabled={updating} className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
              <FiSave className="w-4 h-4 mr-2" />
              {updating ? 'Updating...' : 'Update Profile'}
            </button>
          </form>
        )}

        {/* Business Tab */}
        {activeTab === 'business' && (
          <form onSubmit={handleBusinessUpdate} className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Business Information</h3>
            <p className="text-sm text-gray-600 mb-6">This information will be displayed in search results and invoices</p>
            {['businessName', 'phoneNumber', 'address', 'description'].map(key => (
              <div key={key}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {key === 'businessName' && 'Business Name'}
                  {key === 'phoneNumber' && 'Phone Number'}
                  {key === 'address' && 'Business Address'}
                  {key === 'description' && 'Business Description'}
                </label>
                {key === 'description' ? (
                  <textarea
                    value={businessData[key]}
                    onChange={e => setBusinessData(prev => ({ ...prev, [key]: e.target.value }))}
                    className="input-field"
                    rows="4"
                    placeholder="Describe your business and services"
                  />
                ) : (
                  <input
                    type={key === 'phoneNumber' ? 'tel' : 'text'}
                    value={businessData[key]}
                    onChange={e => setBusinessData(prev => ({ ...prev, [key]: e.target.value }))}
                    className="input-field"
                    placeholder={`Enter your ${key.replace(/([A-Z])/g, ' $1').toLowerCase()}`}
                  />
                )}
              </div>
            ))}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Currency</label>
              <div className="flex items-center gap-3 mb-4">
                <select
                  value={user?.preferredCurrency || ''}
                  onChange={(e) => setUser(prev => ({ ...prev, preferredCurrency: e.target.value }))}
                  className="input-field w-60"
                >
                  <option value="">Use Admin Default</option>
                  {currencies.map(c => (
                    <option key={c.code} value={c.code}>{c.code} - {c.name}</option>
                  ))}
                </select>
                <button
                  onClick={async () => {
                    setUpdating(true)
                    try {
                      await axios.put('/users/preferred-currency', { preferredCurrency: user.preferredCurrency })
                      toast.success('Preferred currency updated')
                    } catch (err) {
                      toast.error('Error updating preferred currency')
                    } finally {
                      setUpdating(false)
                    }
                  }}
                  className="btn-primary px-4 py-2"
                >
                  Save
                </button>
              </div>

              {user?.adminConfig?.isAdmin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Admin Default Currency</label>
                  <div className="flex items-center gap-3">
                    <select
                      value={user?.adminConfig?.defaultCurrency || ''}
                      onChange={(e) => setUser(prev => ({ ...prev, adminConfig: { ...prev.adminConfig, defaultCurrency: e.target.value } }))}
                      className="input-field w-60"
                    >
                      {currencies.map(c => (
                        <option key={c.code} value={c.code}>{c.code} - {c.name}</option>
                      ))}
                    </select>
                    <button
                      onClick={async () => {
                        setUpdating(true)
                        try {
                          await axios.put('/users/admin/default-currency', { defaultCurrency: user.adminConfig.defaultCurrency })
                          toast.success('Admin default currency updated')
                        } catch (err) {
                          toast.error('Error updating admin default currency')
                        } finally {
                          setUpdating(false)
                        }
                      }}
                      className="btn-primary px-4 py-2"
                    >
                      Save
                    </button>
                  </div>
                </div>
              )}
            </div>

            <button type="submit" disabled={updating} className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
              <FiSave className="w-4 h-4 mr-2" />
              {updating ? 'Updating...' : 'Update Business Info'}
            </button>
          </form>
        )}

     {/* Bank Tab */}
    {activeTab === 'bank' && (
  <form onSubmit={handleBankUpdate} className="space-y-6">
    <h3 className="text-lg font-medium text-gray-900 mb-2">Bank Details</h3>
    <p className="text-sm text-gray-600 mb-6">
      These details will be included in unpaid invoices for payment processing.
    </p>

    {[
      'bankName',
      'accountName',
      'accountNumber',
      'IBAN',
      'SWIFT',
      'routingNumber',
      'sortCode',
      'IFSC',
      'BSB',
      'transitNumber',
      'bankCode',
      'branchName',
      'branchAddress',
      'country',
      'currency',
      'accountType',
      'otherIdentifiers'
    ].map(key => (
      <div key={key}>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {key === 'bankName' && 'Bank Name'}
          {key === 'accountName' && 'Account Name'}
          {key === 'accountNumber' && 'Account Number'}
          {key === 'IBAN' && 'IBAN'}
          {key === 'SWIFT' && 'SWIFT/BIC'}
          {key === 'routingNumber' && 'Routing Number'}
          {key === 'sortCode' && 'Sort Code'}
          {key === 'IFSC' && 'IFSC'}
          {key === 'BSB' && 'BSB'}
          {key === 'transitNumber' && 'Transit Number'}
          {key === 'bankCode' && 'Bank Code'}
          {key === 'branchName' && 'Branch Name'}
          {key === 'branchAddress' && 'Branch Address'}
          {key === 'country' && 'Country'}
          {key === 'currency' && 'Currency'}
          {key === 'accountType' && 'Account Type'}
          {key === 'otherIdentifiers' && 'Other Identifiers'}
        </label>

        {/* Input is always empty by default; value comes from bankData only */}
        <input
          type="text"
          value={bankData[key]} 
          onChange={e => setBankData(prev => ({ ...prev, [key]: e.target.value }))}
          className="input-field"
          placeholder={`Enter ${key.replace(/([A-Z])/g, ' $1').toLowerCase()}`}
        />
      </div>
    ))}

    <button
      type="submit"
      disabled={updating}
      className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <FiSave className="w-4 h-4 mr-2" />
      {updating ? 'Updating...' : 'Update Bank Details'}
    </button>
  </form>
    )}

        {/* Address Tab */}
        {activeTab === 'address' && (
          <form onSubmit={handleAddressUpdate} className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Billing Address</h3>
            <p className="text-sm text-gray-600 mb-6">This address will appear on your invoices and official documents</p>
            {['street', 'city', 'state', 'zipCode', 'country'].map(key => (
              <div key={key}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {key === 'street' && 'Street Address'}
                  {key === 'city' && 'City'}
                  {key === 'state' && 'State/Province'}
                  {key === 'zipCode' && 'ZIP/Postal Code'}
                  {key === 'country' && 'Country'}
                </label>
                <input
                  type="text"
                  value={addressData[key]}
                  onChange={e => setAddressData(prev => ({ ...prev, [key]: e.target.value }))}
                  className="input-field"
                  placeholder={`Enter ${key.replace(/([A-Z])/g, ' $1').toLowerCase()}`}
                />
              </div>
            ))}
            <button type="submit" disabled={updating} className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
              <FiSave className="w-4 h-4 mr-2" />
              {updating ? 'Updating...' : 'Update Billing Address'}
            </button>
          </form>
        )}

        {/* Change Password Tab */}
        {activeTab === 'password' && (
          <form onSubmit={handlePasswordChange} className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Change Login Password</h3>
            <p className="text-sm text-gray-600 mb-6">Update the main account password used for login</p>
            {['currentPassword', 'newPassword', 'confirmPassword'].map(key => (
              <div key={key}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {key === 'currentPassword' && 'Current Password'}
                  {key === 'newPassword' && 'New Password'}
                  {key === 'confirmPassword' && 'Confirm New Password'}
                </label>
                <input
                  type="password"
                  required
                  value={passwordData[key]}
                  onChange={e => setPasswordData(prev => ({ ...prev, [key]: e.target.value }))}
                  className="input-field"
                  placeholder={`Enter ${key.replace(/([A-Z])/g, ' $1')}`}
                />
              </div>
            ))}
            <button type="submit" disabled={updating} className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
              <FiLock className="w-4 h-4 mr-2" />
              {updating ? 'Changing Password...' : 'Change Password'}
            </button>
          </form>
        )}

        {/* Admin Password Tab */}
        {activeTab === 'admin-password' && (
          <div className="space-y-6">
            <form onSubmit={handleAdminPasswordChange} className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Change Admin Password</h3>
              <p className="text-sm text-gray-600 mb-6">Update the admin password used to access admin features (you may use your current login password if you do not remember your admin password)</p>
              {['currentAdminPassword', 'newAdminPassword', 'confirmAdminPassword'].map(key => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {key === 'currentAdminPassword' && 'Current Admin Password'}
                    {key === 'newAdminPassword' && 'New Admin Password'}
                    {key === 'confirmAdminPassword' && 'Confirm New Admin Password'}
                  </label>
                  <input
                    type="password"
                    required
                    value={adminPasswordData[key]}
                    onChange={e => setAdminPasswordData(prev => ({ ...prev, [key]: e.target.value }))}
                    className="input-field"
                    placeholder={`Enter ${key.replace(/([A-Z])/g, ' $1')}`}
                  />
                </div>
              ))}
              <button type="submit" disabled={updating} className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
                <FiKey className="w-4 h-4 mr-2" />
                {updating ? 'Changing Admin Password...' : 'Change Admin Password'}
              </button>
            </form>

            <form onSubmit={handleAdminResetLogin} className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Reset Login Password (Admin)</h3>
              <p className="text-sm text-gray-600 mb-6">Reset the main login password for this account using your admin password (will send notification to the user)</p>
              {['currentAdminPassword', 'newLoginPassword', 'confirmNewLoginPassword'].map(key => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {key === 'currentAdminPassword' && 'Current Admin Password'}
                    {key === 'newLoginPassword' && 'New Login Password'}
                    {key === 'confirmNewLoginPassword' && 'Confirm New Login Password'}
                  </label>
                  <input
                    type="password"
                    required
                    value={adminResetData[key]}
                    onChange={e => setAdminResetData(prev => ({ ...prev, [key]: e.target.value }))}
                    className="input-field"
                    placeholder={`Enter ${key.replace(/([A-Z])/g, ' $1')}`}
                  />
                </div>
              ))}
              <button type="submit" disabled={updating} className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
                <FiLock className="w-4 h-4 mr-2" />
                {updating ? 'Resetting Login Password...' : 'Reset Login Password'}
              </button>
            </form>
          </div>
        )}

        {/* Notepad Security Tab */}
        {activeTab === 'notepad-security' && (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Notepad Security</h3>
            <p className="text-sm text-gray-600 mb-6">Protect your notepad with a password. Users must enter this password to view and access your notes.</p>

            {/* Status */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-900">
                <strong>Status:</strong> {notepadPasswordData.hasPasswordSet ? 'Password is set' : 'No password set'}
              </p>
              {notepadPasswordData.hasPasswordSet && notepadPasswordData.createdAt && (
                <p className="text-xs text-blue-700 mt-1">
                  Set on {new Date(notepadPasswordData.createdAt).toLocaleDateString()}
                </p>
              )}
            </div>

            {/* Generated Password Display */}
            {notepadPasswordData.generatedPassword && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm font-medium text-green-900 mb-3">✓ Password Generated</p>
                <p className="text-xs text-green-700 mb-3">Save this password securely. You will not be able to view it again.</p>
                <div className="flex items-center gap-2 bg-white border border-green-300 rounded p-3">
                  <input
                    type={notepadPasswordData.showPassword ? 'text' : 'password'}
                    value={notepadPasswordData.generatedPassword}
                    readOnly
                    className="flex-1 bg-transparent text-sm font-mono outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setNotepadPasswordData(prev => ({ ...prev, showPassword: !prev.showPassword }))}
                    className="text-gray-600 hover:text-gray-700 text-xs font-medium whitespace-nowrap px-2"
                  >
                    {notepadPasswordData.showPassword ? 'Hide' : 'Show'}
                  </button>
                  <button
                    type="button"
                    onClick={copyNotepadPassword}
                    className="text-green-600 hover:text-green-700 text-sm font-medium whitespace-nowrap"
                  >
                    Copy
                  </button>
                </div>
              </div>
            )}

            {/* Set Password Button */}
            {!notepadPasswordData.hasPasswordSet && (
              <div className="flex gap-4 flex-wrap">
                <button
                  onClick={handleSetNotepadPassword}
                  disabled={updating}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <FiKey className="w-4 h-4" />
                  {updating ? 'Setting...' : 'Set Notepad Password'}
                </button>
              </div>
            )}

            {/* Reset Password Form */}
            {notepadPasswordData.hasPasswordSet && (
              <div className="space-y-4">
                <div className="flex gap-4 flex-wrap">
                  <button
                    onClick={() => setNotepadPasswordData(prev => ({ ...prev, showResetForm: !prev.showResetForm }))}
                    className="btn-primary flex items-center gap-2"
                  >
                    <FiKey className="w-4 h-4" />
                    {notepadPasswordData.showResetForm ? 'Cancel Reset' : 'Reset Password'}
                  </button>
                </div>

                {notepadPasswordData.showResetForm && (
                  <form onSubmit={handleResetNotepadPassword} className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
                    <h4 className="text-sm font-medium text-gray-900">Reset Notepad Password</h4>
                    <p className="text-xs text-gray-600">Enter your current password and a new password to reset your notepad security.</p>

                    {/* Current Password */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Current Notepad Password</label>
                      <div className="relative">
                        <input
                          type={notepadResetData.showCurrent ? 'text' : 'password'}
                          required
                          value={notepadResetData.currentPassword}
                          onChange={e => setNotepadResetData(prev => ({ ...prev, currentPassword: e.target.value }))}
                          className="input-field pr-10"
                          placeholder="Enter current password"
                        />
                        <button
                          type="button"
                          onClick={() => setNotepadResetData(prev => ({ ...prev, showCurrent: !prev.showCurrent }))}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600 hover:text-gray-700 text-xs font-medium"
                        >
                          {notepadResetData.showCurrent ? 'Hide' : 'Show'}
                        </button>
                      </div>
                    </div>

                    {/* New Password */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">New Notepad Password</label>
                      <div className="relative">
                        <input
                          type={notepadResetData.showNew ? 'text' : 'password'}
                          required
                          value={notepadResetData.newPassword}
                          onChange={e => setNotepadResetData(prev => ({ ...prev, newPassword: e.target.value }))}
                          className="input-field pr-10"
                          placeholder="Enter new password"
                        />
                        <button
                          type="button"
                          onClick={() => setNotepadResetData(prev => ({ ...prev, showNew: !prev.showNew }))}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600 hover:text-gray-700 text-xs font-medium"
                        >
                          {notepadResetData.showNew ? 'Hide' : 'Show'}
                        </button>
                      </div>
                    </div>

                    {/* Confirm New Password */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
                      <input
                        type="password"
                        required
                        value={notepadResetData.confirmPassword}
                        onChange={e => setNotepadResetData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                        className="input-field"
                        placeholder="Confirm new password"
                      />
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-4 pt-4">
                      <button
                        type="submit"
                        disabled={updating}
                        className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {updating ? 'Resetting...' : 'Reset Password'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setNotepadPasswordData(prev => ({ ...prev, showResetForm: false }))
                          setNotepadResetData({
                            currentPassword: '',
                            newPassword: '',
                            confirmPassword: '',
                            showCurrent: false,
                            showNew: false
                          })
                        }}
                        className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}

            {/* Info Box */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-700 space-y-2">
              <p><strong>How it works:</strong></p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>Only you can set or change the notepad password from this page</li>
                <li>Users must enter the password to view your notepad</li>
                <li>Each password is unique and randomly generated for security</li>
                <li>Users cannot set, view, or change the password themselves</li>
              </ul>
            </div>
          </div>
        )}

        {activeTab === 'company-logo' && (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Company Logo</h3>
            <p className="text-sm text-gray-600 mb-6">Upload your company logo to be displayed on all generated invoices</p>

            <form onSubmit={handleLogoUpload} className="space-y-6">
              {/* Logo Preview */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">Logo Preview</label>
                {companyLogo ? (
                  <div className="border-2 border-gray-300 rounded-lg p-6 bg-gray-50 flex items-center justify-center">
                    <img
                      src={companyLogo}
                      alt="Company Logo"
                      className="max-h-48 max-w-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 bg-gray-50 flex flex-col items-center justify-center text-center">
                    <FiBriefcase className="w-12 h-12 text-gray-400 mb-3" />
                    <p className="text-gray-600 text-sm mb-1">No logo uploaded yet</p>
                    <p className="text-gray-500 text-xs">Upload a logo to display on invoices</p>
                  </div>
                )}
              </div>

              {/* File Upload */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">Upload Logo Image</label>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <label className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors bg-white">
                      <FiCamera className="w-4 h-4 mr-2 text-gray-600" />
                      <span className="text-sm font-medium text-gray-700">Choose File</span>
                      <input
                        id="logo-file-input"
                        type="file"
                        accept="image/*"
                        onChange={handleLogoFileChange}
                        className="hidden"
                      />
                    </label>
                  </div>
                  {logoFile && (
                    <span className="text-sm text-gray-600">
                      {logoFile.name}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500">
                  Supported formats: JPG, PNG, GIF, WebP. Max size: 5MB
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 flex-wrap">
                {logoFile && (
                  <button
                    type="submit"
                    disabled={updating}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <FiSave className="w-4 h-4" />
                    {updating ? 'Uploading...' : 'Upload Logo'}
                  </button>
                )}
                {companyLogo && (
                  <button
                    type="button"
                    onClick={handleLogoDelete}
                    disabled={updating}
                    className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Delete Logo
                  </button>
                )}
              </div>
            </form>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900 space-y-2">
              <p><strong>Logo Guidelines:</strong></p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>Logo will be displayed at the top of all generated invoices</li>
                <li>Recommended size: 400x200 pixels or larger (aspect ratio 2:1)</li>
                <li>Recommended format: PNG with transparent background for best appearance</li>
                <li>File size should not exceed 5MB</li>
                <li>Upload a new logo to replace the current one</li>
                <li>Delete the logo to remove it from future invoices</li>
              </ul>
            </div>
          </div>
        )}

        {activeTab === 'preferences' && (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Preferences</h3>
            <p className="text-sm text-gray-600 mb-6">Manage your personal preferences including timezone for accurate time display.</p>

            {/* Timezone Selection */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">Preferred Timezone</label>
              <p className="text-xs text-gray-600 mb-3">Select your timezone to ensure all scheduled notes and notifications display the correct local time.</p>
              
              <div className="relative">
                <div className="flex items-center gap-2 bg-white border border-gray-300 rounded-lg p-3">
                  <FiSearch className="w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search timezone (e.g., New York, Tokyo, London)..."
                    value={timezoneSearch}
                    onChange={(e) => {
                      setTimezoneSearch(e.target.value)
                      setShowTimezoneDropdown(true)
                    }}
                    onFocus={() => setShowTimezoneDropdown(true)}
                    className="flex-1 bg-transparent text-sm outline-none"
                  />
                </div>

                {/* Timezone Dropdown */}
                {showTimezoneDropdown && filteredTimezones.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 max-h-64 overflow-y-auto">
                    {filteredTimezones.slice(0, 100).map((tz, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          setPreferredTimezone(tz.value)
                          setTimezoneSearch(tz.label)
                          setShowTimezoneDropdown(false)
                        }}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors flex items-center justify-between ${
                          preferredTimezone === tz.value ? 'bg-blue-50' : ''
                        }`}
                      >
                        <span>{tz.label}</span>
                        {preferredTimezone === tz.value && <span className="text-blue-600">✓</span>}
                      </button>
                    ))}
                  </div>
                )}

                {/* Overlay to close dropdown */}
                {showTimezoneDropdown && (
                  <div
                    className="fixed inset-0 z-0"
                    onClick={() => setShowTimezoneDropdown(false)}
                  />
                )}
              </div>

              {/* Selected Timezone Display */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-900">
                  <strong>Current Selection:</strong> {
                    WORLD_TIMEZONES.find(tz => tz.value === preferredTimezone)?.label || preferredTimezone
                  }
                </p>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex gap-4">
              <button
                onClick={handleTimezoneUpdate}
                disabled={updating || preferredTimezone === user?.preferredTimezone}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <FiSave className="w-4 h-4" />
                {updating ? 'Updating...' : 'Save Preference'}
              </button>
            </div>

            {/* Info Box */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-700 space-y-2">
              <p><strong>Why this matters:</strong></p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>Your timezone is used to display scheduled notes and notifications in your local time</li>
                <li>All times are stored in UTC but displayed in your selected timezone</li>
                <li>Daylight saving time is automatically handled for your timezone</li>
                <li>Changes take effect immediately on your next action</li>
              </ul>
            </div>
          </div>
        )}

        {/* Account & Security Tab */}
        {activeTab === 'account-security' && (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Account & Security</h3>
            <p className="text-sm text-gray-600 mb-6">Manage account security and permanently delete your account if needed.</p>

            {/* Delete Account Section */}
            <div className="border-t border-gray-200 pt-6">
              <div className="flex items-start gap-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <FiAlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-medium text-red-900 mb-2">Delete Account Permanently</h4>
                  <p className="text-sm text-red-800 mb-4">
                    Deleting your account will permanently remove all your data from our system, including:
                  </p>
                  <ul className="text-sm text-red-800 space-y-1 mb-4 ml-4 list-disc">
                    <li>Profile information and settings</li>
                    <li>All items and products</li>
                    <li>Orders and payment history</li>
                    <li>Websites and website content</li>
                    <li>Notes and documents</li>
                    <li>Bank details and billing addresses</li>
                  </ul>
                  <p className="text-sm text-red-800 font-medium mb-4">This action cannot be undone.</p>
                  <button
                    onClick={() => setShowDeleteModal(true)}
                    className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                  >
                    <FiTrash2 className="w-4 h-4" />
                    Delete My Account
                  </button>
                </div>
              </div>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
                  <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                      <FiAlertTriangle className="w-6 h-6 text-red-600" />
                      <h3 className="text-lg font-bold text-gray-900">Delete Account?</h3>
                    </div>
                  </div>

                  <form onSubmit={handleDeleteAccount} className="p-6 space-y-4">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <p className="text-sm text-red-900">
                        <strong>Warning:</strong> This will permanently delete your account and all associated data. This action cannot be reversed.
                      </p>
                    </div>

                    {/* Password Confirmation */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Enter your password to confirm
                      </label>
                      <input
                        type="password"
                        value={deletePassword}
                        onChange={(e) => setDeletePassword(e.target.value)}
                        placeholder="Enter your password"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                        required
                      />
                    </div>

                    {/* Confirmation Text */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Type <strong>DELETE MY ACCOUNT</strong> to confirm
                      </label>
                      <input
                        type="text"
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                        placeholder="DELETE MY ACCOUNT"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                        required
                      />
                    </div>

                    {/* Buttons */}
                    <div className="flex gap-3 pt-4">
                      <button
                        type="button"
                        onClick={() => {
                          setShowDeleteModal(false)
                          setDeletePassword('')
                          setDeleteConfirmText('')
                        }}
                        className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-900 font-medium rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={isDeleting || deleteConfirmText !== 'DELETE MY ACCOUNT'}
                        className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        <FiTrash2 className="w-4 h-4" />
                        {isDeleting ? 'Deleting...' : 'Delete Account'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}





// import { useState, useEffect } from 'react'
// import { FiUser, FiMail, FiCamera, FiSave, FiCreditCard, FiMapPin, FiLock, FiKey, FiBriefcase } from 'react-icons/fi'
// import axios from 'axios'
// import toast from 'react-hot-toast'

// export default function UserAdminSettings() {
//   const [user, setUser] = useState(null)
//   const [loading, setLoading] = useState(true)
//   const [activeTab, setActiveTab] = useState('profile')
//   const [profileData, setProfileData] = useState({
//     firstName: '',
//     lastName: '',
//     profileImageUrl: ''
//   })
//   const [businessData, setBusinessData] = useState({
//     businessName: '',
//     phoneNumber: '',
//     address: '',
//     description: ''
//   })
//   const [bankData, setBankData] = useState({
//     bankName: '',
//     accountName: '',
//     accountNumber: '',
//     routingNumber: '',
//     swiftCode: ''
//   })
//   const [addressData, setAddressData] = useState({
//     street: '',
//     city: '',
//     state: '',
//     zipCode: '',
//     country: ''
//   })
//   const [passwordData, setPasswordData] = useState({
//     currentPassword: '',
//     newPassword: '',
//     confirmPassword: ''
//   })
//   const [adminPasswordData, setAdminPasswordData] = useState({
//     currentAdminPassword: '',
//     newAdminPassword: '',
//     confirmAdminPassword: ''
//   })
//   const [profileImageFile, setProfileImageFile] = useState(null)
//   const [updating, setUpdating] = useState(false)

//   useEffect(() => {
//     fetchProfile()
//   }, [])

//   const fetchProfile = async () => {
//     try {
//       const response = await axios.get('/auth/profile')
//       const userData = response.data.user
//       setUser(userData)
      
//       setProfileData({
//         firstName: userData.firstName || '',
//         lastName: userData.lastName || '',
//         profileImageUrl: userData.profileImage || ''
//       })
      
//       setBusinessData({
//         businessName: userData.businessInfo?.businessName || '',
//         phoneNumber: userData.businessInfo?.phoneNumber || '',
//         address: userData.businessInfo?.address || '',
//         description: userData.businessInfo?.description || ''
//       })
      
//       setBankData({
//         bankName: userData.bankDetails?.bankName || '',
//         accountName: userData.bankDetails?.accountName || '',
//         accountNumber: userData.bankDetails?.accountNumber || '',
//         routingNumber: userData.bankDetails?.routingNumber || '',
//         swiftCode: userData.bankDetails?.swiftCode || ''
//       })
      
//       setAddressData({
//         street: userData.billingAddress?.street || '',
//         city: userData.billingAddress?.city || '',
//         state: userData.billingAddress?.state || '',
//         zipCode: userData.billingAddress?.zipCode || '',
//         country: userData.billingAddress?.country || ''
//       })
//     } catch (error) {
//       toast.error('Error loading profile')
//     } finally {
//       setLoading(false)
//     }
//   }

//   const handleProfileUpdate = async (e) => {
//     e.preventDefault()
//     setUpdating(true)

//     try {
//       const formData = new FormData()
//       formData.append('firstName', profileData.firstName)
//       formData.append('lastName', profileData.lastName)
//       formData.append('profileImageUrl', profileData.profileImageUrl)
      
//       if (profileImageFile) {
//         formData.append('profileImage', profileImageFile)
//       }

//       const response = await axios.put('/users/profile', formData, {
//         headers: { 'Content-Type': 'multipart/form-data' }
//       })
      
//       setUser(response.data.user)
//       toast.success('Profile updated successfully')
//       setProfileImageFile(null)
//     } catch (error) {
//       toast.error(error.response?.data?.message || 'Error updating profile')
//     } finally {
//       setUpdating(false)
//     }
//   }

//   const handleBusinessUpdate = async (e) => {
//     e.preventDefault()
//     setUpdating(true)

//     try {
//       await axios.put('/users/business-info', businessData)
//       toast.success('Business information updated successfully')
//       fetchProfile() // Refresh profile data
//     } catch (error) {
//       toast.error(error.response?.data?.message || 'Error updating business information')
//     } finally {
//       setUpdating(false)
//     }
//   }

//   const handleBankUpdate = async (e) => {
//     e.preventDefault()
//     setUpdating(true)

//     try {
//       await axios.put('/users/bank-details', bankData)
//       toast.success('Bank details updated successfully')
//     } catch (error) {
//       toast.error(error.response?.data?.message || 'Error updating bank details')
//     } finally {
//       setUpdating(false)
//     }
//   }

//   const handleAddressUpdate = async (e) => {
//     e.preventDefault()
//     setUpdating(true)

//     try {
//       await axios.put('/users/billing-address', addressData)
//       toast.success('Billing address updated successfully')
//     } catch (error) {
//       toast.error(error.response?.data?.message || 'Error updating billing address')
//     } finally {
//       setUpdating(false)
//     }
//   }

//   const handlePasswordChange = async (e) => {
//     e.preventDefault()
    
//     if (passwordData.newPassword !== passwordData.confirmPassword) {
//       toast.error('New passwords do not match')
//       return
//     }

//     if (passwordData.newPassword.length < 6) {
//       toast.error('New password must be at least 6 characters long')
//       return
//     }

//     setUpdating(true)

//     try {
//       await axios.put('/users/change-password', {
//         currentPassword: passwordData.currentPassword,
//         newPassword: passwordData.newPassword
//       })
      
//       toast.success('Password changed successfully')
//       setPasswordData({
//         currentPassword: '',
//         newPassword: '',
//         confirmPassword: ''
//       })
//     } catch (error) {
//       toast.error(error.response?.data?.message || 'Error changing password')
//     } finally {
//       setUpdating(false)
//     }
//   }

//   const handleAdminPasswordChange = async (e) => {
//     e.preventDefault()
    
//     if (adminPasswordData.newAdminPassword !== adminPasswordData.confirmAdminPassword) {
//       toast.error('New admin passwords do not match')
//       return
//     }

//     if (adminPasswordData.newAdminPassword.length < 6) {
//       toast.error('New admin password must be at least 6 characters long')
//       return
//     }

//     setUpdating(true)

//     try {
//       await axios.put('/admin/change-password', {
//         currentAdminPassword: adminPasswordData.currentAdminPassword,
//         newAdminPassword: adminPasswordData.newAdminPassword
//       })
      
//       toast.success('Admin password changed successfully')
//       setAdminPasswordData({
//         currentAdminPassword: '',
//         newAdminPassword: '',
//         confirmAdminPassword: ''
//       })
//     } catch (error) {
//       toast.error(error.response?.data?.message || 'Error changing admin password')
//     } finally {
//       setUpdating(false)
//     }
//   }

//   const handleImageFileChange = (e) => {
//     const file = e.target.files[0]
//     if (file) {
//       if (file.size > 5 * 1024 * 1024) {
//         toast.error('Image size should be less than 5MB')
//         return
//       }
//       setProfileImageFile(file)
      
//       // Preview the image
//       const reader = new FileReader()
//       reader.onload = (e) => {
//         setProfileData(prev => ({ ...prev, profileImageUrl: e.target.result }))
//       }
//       reader.readAsDataURL(file)
//     }
//   }

//   const tabs = [
//     { id: 'profile', name: 'Profile Info', icon: FiUser },
//     { id: 'business', name: 'Business Info', icon: FiBriefcase },
//     { id: 'bank', name: 'Bank Details', icon: FiCreditCard },
//     { id: 'address', name: 'Billing Address', icon: FiMapPin },
//     { id: 'password', name: 'Change Password', icon: FiLock },
//     { id: 'admin-password', name: 'Admin Password', icon: FiKey }
//   ]

//   if (loading) {
//     return (
//       <div className="animate-pulse">
//         <div className="card h-96 bg-gray-200"></div>
//       </div>
//     )
//   }

//   return (
//     <div className="max-w-4xl mx-auto">
//       <div className="mb-8">
//         <h1 className="text-2xl font-bold text-gray-900 mb-2">Admin Settings</h1>
//         <p className="text-gray-600">Manage all profile information and settings as User Admin</p>
//       </div>

//       <div className="card">
//         {/* Tab Navigation */}
//         <div className="border-b border-gray-200 mb-6">
//           <nav className="-mb-px flex space-x-8 overflow-x-auto">
//             {tabs.map((tab) => {
//               const Icon = tab.icon
//               return (
//                 <button
//                   key={tab.id}
//                   onClick={() => setActiveTab(tab.id)}
//                   className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
//                     activeTab === tab.id
//                       ? 'border-primary-500 text-primary-600'
//                       : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
//                   }`}
//                 >
//                   <Icon className="w-4 h-4 mr-2" />
//                   {tab.name}
//                 </button>
//               )
//             })}
//           </nav>
//         </div>

//         {/* Profile Info Tab */}
//         {activeTab === 'profile' && (
//           <form onSubmit={handleProfileUpdate} className="space-y-6">
//             <div className="flex items-center space-x-6">
//               <div className="relative">
//                 <img
//                   src={profileData.profileImageUrl || `https://ui-avatars.com/api/?name=${profileData.firstName}+${profileData.lastName}&background=3b82f6&color=fff`}
//                   alt="Profile"
//                   className="w-24 h-24 rounded-full object-cover border-4 border-gray-200"
//                 />
//                 <label className="absolute bottom-0 right-0 bg-primary-600 text-white p-2 rounded-full cursor-pointer hover:bg-primary-700 transition-colors">
//                   <FiCamera className="w-4 h-4" />
//                   <input
//                     type="file"
//                     accept="image/*"
//                     onChange={handleImageFileChange}
//                     className="hidden"
//                   />
//                 </label>
//               </div>
//               <div className="flex-1">
//                 <h3 className="text-lg font-medium text-gray-900">Profile Picture</h3>
//                 <p className="text-sm text-gray-600">Upload a new profile picture or enter an image URL</p>
//                 <div className="mt-2">
//                   <input
//                     type="url"
//                     placeholder="Or enter image URL"
//                     value={profileData.profileImageUrl}
//                     onChange={(e) => setProfileData(prev => ({ ...prev, profileImageUrl: e.target.value }))}
//                     className="input-field"
//                   />
//                 </div>
//               </div>
//             </div>

//             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   First Name
//                 </label>
//                 <input
//                   type="text"
//                   required
//                   value={profileData.firstName}
//                   onChange={(e) => setProfileData(prev => ({ ...prev, firstName: e.target.value }))}
//                   className="input-field"
//                 />
//               </div>
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Last Name
//                 </label>
//                 <input
//                   type="text"
//                   required
//                   value={profileData.lastName}
//                   onChange={(e) => setProfileData(prev => ({ ...prev, lastName: e.target.value }))}
//                   className="input-field"
//                 />
//               </div>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Email Address
//               </label>
//               <input
//                 type="email"
//                 value={user?.email || ''}
//                 disabled
//                 className="input-field bg-gray-50 cursor-not-allowed"
//               />
//               <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
//             </div>

//             <button
//               type="submit"
//               disabled={updating}
//               className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
//             >
//               <FiSave className="w-4 h-4 mr-2" />
//               {updating ? 'Updating...' : 'Update Profile'}
//             </button>
//           </form>
//         )}

//         {/* Business Info Tab */}
//         {activeTab === 'business' && (
//           <form onSubmit={handleBusinessUpdate} className="space-y-6">
//             <div>
//               <h3 className="text-lg font-medium text-gray-900 mb-2">Business Information</h3>
//               <p className="text-sm text-gray-600 mb-6">
//                 This information will be displayed in search results and invoices
//               </p>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Business Name
//               </label>
//               <input
//                 type="text"
//                 value={businessData.businessName}
//                 onChange={(e) => setBusinessData(prev => ({ ...prev, businessName: e.target.value }))}
//                 className="input-field"
//                 placeholder="Enter your business name"
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Phone Number
//               </label>
//               <input
//                 type="tel"
//                 value={businessData.phoneNumber}
//                 onChange={(e) => setBusinessData(prev => ({ ...prev, phoneNumber: e.target.value }))}
//                 className="input-field"
//                 placeholder="Enter business phone number"
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Business Address
//               </label>
//               <input
//                 type="text"
//                 value={businessData.address}
//                 onChange={(e) => setBusinessData(prev => ({ ...prev, address: e.target.value }))}
//                 className="input-field"
//                 placeholder="Enter business address"
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Business Description
//               </label>
//               <textarea
//                 value={businessData.description}
//                 onChange={(e) => setBusinessData(prev => ({ ...prev, description: e.target.value }))}
//                 className="input-field"
//                 rows="4"
//                 placeholder="Describe your business and services"
//               />
//             </div>

//             <button
//               type="submit"
//               disabled={updating}
//               className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
//             >
//               <FiSave className="w-4 h-4 mr-2" />
//               {updating ? 'Updating...' : 'Update Business Info'}
//             </button>
//           </form>
//         )}

//         {/* Bank Details Tab */}
//         {activeTab === 'bank' && (
//           <form onSubmit={handleBankUpdate} className="space-y-6">
//             <div>
//               <h3 className="text-lg font-medium text-gray-900 mb-2">Bank Details</h3>
//               <p className="text-sm text-gray-600 mb-6">
//                 These details will be included in unpaid invoices for payment processing
//               </p>
//             </div>

//             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Bank Name
//                 </label>
//                 <input
//                   type="text"
//                   value={bankData.bankName}
//                   onChange={(e) => setBankData(prev => ({ ...prev, bankName: e.target.value }))}
//                   className="input-field"
//                   placeholder="Enter bank name"
//                 />
//               </div>
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Account Name
//                 </label>
//                 <input
//                   type="text"
//                   value={bankData.accountName}
//                   onChange={(e) => setBankData(prev => ({ ...prev, accountName: e.target.value }))}
//                   className="input-field"
//                   placeholder="Enter account holder name"
//                 />
//               </div>
//             </div>

//             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Account Number
//                 </label>
//                 <input
//                   type="text"
//                   value={bankData.accountNumber}
//                   onChange={(e) => setBankData(prev => ({ ...prev, accountNumber: e.target.value }))}
//                   className="input-field"
//                   placeholder="Enter account number"
//                 />
//               </div>
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Routing Number
//                 </label>
//                 <input
//                   type="text"
//                   value={bankData.routingNumber}
//                   onChange={(e) => setBankData(prev => ({ ...prev, routingNumber: e.target.value }))}
//                   className="input-field"
//                   placeholder="Enter routing number"
//                 />
//               </div>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 SWIFT Code (Optional)
//               </label>
//               <input
//                 type="text"
//                 value={bankData.swiftCode}
//                 onChange={(e) => setBankData(prev => ({ ...prev, swiftCode: e.target.value }))}
//                 className="input-field"
//                 placeholder="Enter SWIFT code for international transfers"
//               />
//             </div>

//             <button
//               type="submit"
//               disabled={updating}
//               className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
//             >
//               <FiSave className="w-4 h-4 mr-2" />
//               {updating ? 'Updating...' : 'Update Bank Details'}
//             </button>
//           </form>
//         )}

//         {/* Billing Address Tab */}
//         {activeTab === 'address' && (
//           <form onSubmit={handleAddressUpdate} className="space-y-6">
//             <div>
//               <h3 className="text-lg font-medium text-gray-900 mb-2">Billing Address</h3>
//               <p className="text-sm text-gray-600 mb-6">
//                 This address will appear on your invoices and official documents
//               </p>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Street Address
//               </label>
//               <input
//                 type="text"
//                 value={addressData.street}
//                 onChange={(e) => setAddressData(prev => ({ ...prev, street: e.target.value }))}
//                 className="input-field"
//                 placeholder="Enter street address"
//               />
//             </div>

//             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   City
//                 </label>
//                 <input
//                   type="text"
//                   value={addressData.city}
//                   onChange={(e) => setAddressData(prev => ({ ...prev, city: e.target.value }))}
//                   className="input-field"
//                   placeholder="Enter city"
//                 />
//               </div>
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   State/Province
//                 </label>
//                 <input
//                   type="text"
//                   value={addressData.state}
//                   onChange={(e) => setAddressData(prev => ({ ...prev, state: e.target.value }))}
//                   className="input-field"
//                   placeholder="Enter state or province"
//                 />
//               </div>
//             </div>

//             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   ZIP/Postal Code
//                 </label>
//                 <input
//                   type="text"
//                   value={addressData.zipCode}
//                   onChange={(e) => setAddressData(prev => ({ ...prev, zipCode: e.target.value }))}
//                   className="input-field"
//                   placeholder="Enter ZIP or postal code"
//                 />
//               </div>
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Country
//                 </label>
//                 <input
//                   type="text"
//                   value={addressData.country}
//                   onChange={(e) => setAddressData(prev => ({ ...prev, country: e.target.value }))}
//                   className="input-field"
//                   placeholder="Enter country"
//                 />
//               </div>
//             </div>

//             <button
//               type="submit"
//               disabled={updating}
//               className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
//             >
//               <FiSave className="w-4 h-4 mr-2" />
//               {updating ? 'Updating...' : 'Update Billing Address'}
//             </button>
//           </form>
//         )}

//         {/* Change Password Tab */}
//         {activeTab === 'password' && (
//           <form onSubmit={handlePasswordChange} className="space-y-6">
//             <div>
//               <h3 className="text-lg font-medium text-gray-900 mb-2">Change Login Password</h3>
//               <p className="text-sm text-gray-600 mb-6">
//                 Update the main account password used for login
//               </p>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Current Password
//               </label>
//               <input
//                 type="password"
//                 required
//                 value={passwordData.currentPassword}
//                 onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
//                 className="input-field"
//                 placeholder="Enter current password"
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 New Password
//               </label>
//               <input
//                 type="password"
//                 required
//                 value={passwordData.newPassword}
//                 onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
//                 className="input-field"
//                 placeholder="Enter new password"
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Confirm New Password
//               </label>
//               <input
//                 type="password"
//                 required
//                 value={passwordData.confirmPassword}
//                 onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
//                 className="input-field"
//                 placeholder="Confirm new password"
//               />
//             </div>

//             <button
//               type="submit"
//               disabled={updating}
//               className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
//             >
//               <FiLock className="w-4 h-4 mr-2" />
//               {updating ? 'Changing Password...' : 'Change Password'}
//             </button>
//           </form>
//         )}

//         {/* Admin Password Tab */}
//         {activeTab === 'admin-password' && (
//           <form onSubmit={handleAdminPasswordChange} className="space-y-6">
//             <div>
//               <h3 className="text-lg font-medium text-gray-900 mb-2">Change Admin Password</h3>
//               <p className="text-sm text-gray-600 mb-6">
//                 Update the admin password used to access admin features
//               </p>
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Current Admin Password
//               </label>
//               <input
//                 type="password"
//                 required
//                 value={adminPasswordData.currentAdminPassword}
//                 onChange={(e) => setAdminPasswordData(prev => ({ ...prev, currentAdminPassword: e.target.value }))}
//                 className="input-field"
//                 placeholder="Enter current admin password"
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 New Admin Password
//               </label>
//               <input
//                 type="password"
//                 required
//                 value={adminPasswordData.newAdminPassword}
//                 onChange={(e) => setAdminPasswordData(prev => ({ ...prev, newAdminPassword: e.target.value }))}
//                 className="input-field"
//                 placeholder="Enter new admin password"
//               />
//             </div>

//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Confirm New Admin Password
//               </label>
//               <input
//                 type="password"
//                 required
//                 value={adminPasswordData.confirmAdminPassword}
//                 onChange={(e) => setAdminPasswordData(prev => ({ ...prev, confirmAdminPassword: e.target.value }))}
//                 className="input-field"
//                 placeholder="Confirm new admin password"
//               />
//             </div>

//             <button
//               type="submit"
//               disabled={updating}
//               className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
//             >
//               <FiKey className="w-4 h-4 mr-2" />
//               {updating ? 'Changing Admin Password...' : 'Change Admin Password'}
//             </button>
//           </form>
//         )}
//       </div>
//     </div>
//   )
// }